import { cx } from "@emotion/css";
import Skeleton from "@mui/material/Skeleton";
import type { LucideIcon } from "lucide-react";
import {
	BookOpen,
	ChevronRight,
	CircleHelp,
	CircleX,
	Fish,
	ShieldCheck,
	Sparkles,
	Star,
	Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import useKeypress from "react-use-keypress";
import type { QuickFilter, SearchType } from "src/api/search";
import { TextPrimaryButton } from "src/components/Default/LekUI/Button";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import Box from "src/components/Default/Main/Box";
import { highlightPhrase } from "src/helpers/highlightSearch";
import isMobile from "src/helpers/isMobile";
import { isLdek, isLek } from "src/helpers/services";
import { useDebounce } from "src/hooks/useDebounce";
import { useGlobalSearch } from "src/hooks/useGlobalSearch";

import { SearchSnippet } from "./SearchSnippet";
import {
	buildSections,
	decksSearchTo,
	questionsSearchTo,
} from "./searchSections";

type Tab = "all" | SearchType;

const TYPE_ICONS: Record<SearchType, LucideIcon> = {
	questions: CircleHelp,
	articles: BookOpen,
	decks: Fish,
};

const QUICK_FILTERS: {
	value: QuickFilter;
	Icon: LucideIcon;
	iconClassName: string;
}[] = [
	{ value: "starred", Icon: Star, iconClassName: "icon-yellow" },
	{ value: "wrongAnswers", Icon: CircleX, iconClassName: "icon-error" },
	{ value: "isUnanswered", Icon: CircleHelp, iconClassName: "icon-primary" },
];

const DECK_CATEGORY_ICONS: Record<string, LucideIcon> = {
	admin: ShieldCheck,
	users: Users,
	essential: Sparkles,
};

const PAGE_SIZE = 10;
const SKELETON_ROWS = 6;
const SEARCH_TYPES: SearchType[] = ["questions", "decks", "articles"];

const hasArticles = () => isLek() || isLdek();

type SearchResultsProps = {
	searchValue: string;
	onClose: () => void;
};

export function SearchResults({ searchValue, onClose }: SearchResultsProps) {
	const { t, i18n } = useTranslation();
	const history = useHistory();
	const [tab, setTab] = useState<Tab>("all");
	const search = useDebounce(searchValue, 400);
	// strony liczone dla pary zakładka+fraza — zmiana którejkolwiek = znowu pierwsza strona
	const pagesKey = `${tab}|${search}`;
	const [loaded, setLoaded] = useState({ key: pagesKey, pages: 1 });
	const pages = loaded.key === pagesKey ? loaded.pages : 1;

	// ponytail: „więcej” = większe take tego samego zapytania (dociąga wszystkie typy);
	// osobne zapytanie per typ z page, jeśli zacznie to ważyć
	const { data, isLoading, isFetching, isPlaceholderData } = useGlobalSearch({
		searchValue: search,
		take: tab === "all" ? 3 : PAGE_SIZE * pages,
	});

	// zakładka, dla której są wyświetlane dane — po zmianie zakładki loader zamiast starych wyników
	// (aktualizacja w renderze zamiast useEffect: https://react.dev/learn/you-might-not-need-an-effect)
	const [dataTab, setDataTab] = useState<Tab>(tab);
	if (data && !isPlaceholderData && dataTab !== tab) setDataTab(tab);
	const isSwitchingTab = dataTab !== tab;

	// zaznaczenie z klawiatury należy do konkretnych wyników — nowe dane/zakładka = brak zaznaczenia
	const [active, setActive] = useState({ data, tab, index: -1 });
	const activeIndex =
		active.data === data && active.tab === tab ? active.index : -1;

	const sections = buildSections({
		data: data ?? {},
		searchValue: search,
		t,
	}).filter(({ type }) => type !== "articles" || hasArticles());
	const visibleSections = sections.filter(
		({ type, items }) => (tab === "all" || tab === type) && items.length > 0,
	);
	const items = visibleSections.flatMap((section) => section.items);
	const formatCount = (count: number) => count.toLocaleString(i18n.language);
	const totalCount = sections.reduce((sum, { count }) => sum + count, 0);

	// nawigacja po wynikach tylko z pola szukania (Esc zamyka sam modal/drawer)
	useKeypress(["ArrowDown", "ArrowUp", "Enter"], (event: KeyboardEvent) => {
		if (!(event.target instanceof HTMLInputElement)) return;
		event.preventDefault();

		if (event.key === "Enter") {
			const target =
				items[activeIndex]?.to ??
				(visibleSections.find(({ type }) => type === tab) ?? visibleSections[0])
					?.showAllTo;
			if (target) {
				history.push(target);
				onClose();
			}
			return;
		}

		const step = event.key === "ArrowDown" ? 1 : -1;
		setActive({
			data,
			tab,
			index: Math.min(Math.max(activeIndex + step, 0), items.length - 1),
		});
	});

	// taby od razu (liczniki dopiero z danymi), żeby nagłówek nie skakał po pierwszym requeście
	const tabs: { value: Tab; count?: number }[] = [
		{ value: "all", count: data && totalCount },
		...SEARCH_TYPES.filter((type) => type !== "articles" || hasArticles()).map(
			(type) => ({
				value: type,
				count: sections.find((section) => section.type === type)?.count,
			}),
		),
	];

	// skróty: pytania — szybkie filtry, fiszki — kategorie z backendu (bez "all");
	// na „Wszystko” w nagłówku każdej sekcji, na zakładce typu pod tabami
	const filterLinksFor = (type: SearchType) =>
		type === "decks"
			? (data?.decks?.availableCategories ?? [])
					.filter(({ value }) => value !== "all")
					.map(({ value, label }) => ({
						key: `decks-${value}`,
						to: decksSearchTo(search, value),
						Icon: DECK_CATEGORY_ICONS[value] ?? Fish,
						iconClassName: "icon-primary",
						label,
					}))
			: type === "questions"
				? QUICK_FILTERS.map(({ value, Icon, iconClassName }) => ({
						key: value,
						to: questionsSearchTo(search, value),
						Icon,
						iconClassName,
						label: t(`globalSearch.filters.${value}`),
					}))
				: [];
	const filterLinks = tab === "all" ? [] : filterLinksFor(tab);

	const renderFilterLinks = (links: ReturnType<typeof filterLinksFor>) =>
		links.map(({ key, to, Icon, iconClassName, label }) => (
			<Link
				key={key}
				to={to}
				onClick={onClose}
				className="global-search__filter"
				data-test-id={`global-search-filter-${key}`}
			>
				<Icon className={cx("icon-super-small", iconClassName)} />
				{label}
			</Link>
		));

	const highlight = (text: string) => highlightPhrase(text, search);
	const emptyText = t("globalSearch.empty", { phrase: search });

	const renderResults = () => {
		if (isLoading || isSwitchingTab) {
			return Array.from({ length: SKELETON_ROWS }, (_, index) => (
				<div key={index} className="flex items-start gap-12 p-12">
					<Skeleton
						variant="rounded"
						width={32}
						height={32}
						className="flex-shrink-0"
					/>
					<div className="flex flex-col gap-4 flex-1">
						<Skeleton variant="text" width="35%" />
						<Skeleton variant="text" width="85%" />
					</div>
				</div>
			));
		}

		if (visibleSections.length === 0) {
			return (
				<p className="size-14-light text-grey text-center p-16 m-0">
					{emptyText}
				</p>
			);
		}

		return visibleSections.map((section) => {
			const Icon = TYPE_ICONS[section.type];

			return (
				<div key={section.type} className="flex flex-col">
					<div className="global-search__section-header">
						<span className="global-search__section-title">
							{section.title} · {formatCount(section.count)}
						</span>
						{tab === "all" && (
							<div className="global-search__section-filters">
								{renderFilterLinks(filterLinksFor(section.type))}
							</div>
						)}
						<Link
							to={section.showAllTo}
							onClick={onClose}
							className="global-search__show-all"
							data-test-id={`global-search-show-all-${section.type}`}
						>
							{section.showAllLabel}
							<ChevronRight className="icon-small" />
						</Link>
					</div>
					{section.items.map((item) => (
						<Link
							key={item.key}
							to={item.to}
							onClick={onClose}
							className={cx("global-search__item", {
								"global-search__item--active": items[activeIndex] === item,
							})}
						>
							<span
								className={`global-search__item-icon search-type--${section.type}`}
							>
								<Icon className="icon-super-small" />
							</span>
							<span className="flex flex-col min-w-0 flex-1">
								<span className="size-12-light text-grey text-ellipsis-1">
									{item.meta}
								</span>
								<span className="size-14-light text-ellipsis-1">
									{highlight(item.title)}
								</span>
								{item.snippet && <SearchSnippet snippet={item.snippet} />}
							</span>
						</Link>
					))}
					{tab !== "all" && section.items.length < section.count && (
						<TextPrimaryButton
							onClick={() => setLoaded({ key: pagesKey, pages: pages + 1 })}
							isLoading={isFetching}
							className="align-self-center mt-8 mb-16"
							data-test-id={`global-search-load-more-${section.type}`}
						>
							{t(
								"globalSearch.loadMore",
								"Załaduj więcej ({{shown}} z {{total}})",
								{
									shown: formatCount(section.items.length),
									total: formatCount(section.count),
								},
							)}
						</TextPrimaryButton>
					)}
				</div>
			);
		});
	};

	return (
		<div className="global-search">
			<div className="global-search__tabs">
				<Box
					className="border-none box-shadow-none p-0"
					headerClassName="mb-0"
					tabsClassName="tabs-scroll-shadow"
					TabsWrapper={ScrollShadowWrapper}
					tabs={tabs.map(({ value, count }) => (
						<>
							{t(`globalSearch.tabs.${value}`)}
							{count !== undefined && (
								<span className="tab-count">{formatCount(count)}</span>
							)}
						</>
					))}
					selectedTab={tabs.findIndex(({ value }) => value === tab)}
					onTabsChange={(index) => setTab(tabs[index].value)}
					tabTestIds={tabs.map(({ value }) => `global-search-tab-${value}`)}
				/>
			</div>
			{filterLinks.length > 0 && (
				<ScrollShadowWrapper>
					<div className="global-search__filters">
						{!isMobile() && (
							<span className="size-12-light text-grey">
								{t("globalSearch.showOnly", "Pokaż tylko:")}
							</span>
						)}
						{renderFilterLinks(filterLinks)}
					</div>
				</ScrollShadowWrapper>
			)}
			<div className="global-search__results">{renderResults()}</div>
			{!isMobile() && (
				<div className="global-search__footer">
					<span>
						<kbd>↑</kbd>
						<kbd>↓</kbd> {t("globalSearch.keys.navigate", "nawigacja")}
					</span>
					<span>
						<kbd>↵</kbd> {t("globalSearch.keys.open", "otwórz")}
					</span>
					<span>
						<kbd>Esc</kbd> {t("globalSearch.keys.close", "zamknij")}
					</span>
				</div>
			)}
		</div>
	);
}
